import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import utils from '../../utils/utils';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import DATE_FORMAT from '../../components/constants/dateFormat';
import apiCalls from '../../utils/apiCalls';

const AdminDeliveryDayViewUsers = ({ filteredOrderBatches, deliveryDay, setModalAddress }) => {
  const [hiddenServiceAreaIds, setHiddenServiceAreaIds] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);

  useEffect(() => {
    getServiceAreas();
  }, []);

  function getServiceAreas() {
    apiCalls.getServiceAreas({}).then((result) => {
      setServiceAreas(result.data.serviceAreas);
    });
  }

  const dobsGroupedByServiceArea = [];
  if (filteredOrderBatches) {
    filteredOrderBatches.forEach((dob) => {
      const serviceAreaId = dob.address.serviceAreaId || 0;
      const existingIndex = dobsGroupedByServiceArea.findIndex(
        (groupedDob) => groupedDob.serviceAreaId === serviceAreaId
      );

      if (existingIndex === -1) {
        dobsGroupedByServiceArea.push({
          serviceAreaId,
          deliveryOrderBatches: [dob],
        });
      } else {
        dobsGroupedByServiceArea[existingIndex].deliveryOrderBatches.push(dob);
      }
    });
  }

  const csvFileTitleDate = deliveryDay
    ? utils.getFormattedDateFromString(deliveryDay.deliveryDate, DATE_FORMAT.deliveryCsv)
    : '';

  return (
    <div>
      <div className="m-b-2">
        <Link className="btn btn-default" to={`/admin/deliveryDay/${deliveryDay ? deliveryDay.id : ''}`}>
          <FormattedMessage id={'delivery.day.users.view.cta.orders'} />
        </Link>
      </div>

      <table className="table table-striped m-b-6">
        <thead>
          <th scope="col">
            <FormattedMessage id={'delivery.day.users.view.table.column.user'} />
          </th>
          <th scope="col">
            <FormattedMessage id={'delivery.day.users.view.table.column.address'} />
          </th>
          <th scope="col">
            <FormattedMessage id={'delivery.day.users.view.table.column.phone'} />
          </th>
          <th scope="col">
            <FormattedMessage id={'delivery.day.users.view.table.column.email'} />
          </th>
        </thead>
        <tbody>
          {dobsGroupedByServiceArea.map((groupedDob) => {
            const isHidden = hiddenServiceAreaIds.indexOf(groupedDob.serviceAreaId) !== -1;
            const serviceArea = serviceAreas.find((sa) => sa.id === groupedDob.serviceAreaId);

            return (
              <>
                <tr>
                  <td className="admin__table-separator" colSpan="3">
                    <h4 className="m-b-2">{serviceArea?.name}</h4>
                    <div
                      className="btn btn-normal-link"
                      onClick={() => {
                        const newHiddenServiceAreaIds = hiddenServiceAreaIds.slice();
                        if (isHidden) {
                          newHiddenServiceAreaIds.splice(
                            newHiddenServiceAreaIds.findIndex((id) => id === groupedDob.serviceAreaId),
                            1
                          );
                        } else {
                          newHiddenServiceAreaIds.push(groupedDob.serviceAreaId);
                        }
                        setHiddenServiceAreaIds(newHiddenServiceAreaIds);
                      }}
                    >
                      <FormattedMessage
                        id={
                          isHidden
                            ? 'delivery.day.users.view.service.area.show'
                            : 'delivery.day.users.view.service.area.hide'
                        }
                      />
                    </div>
                  </td>
                  <td className="admin__table-separator">
                    <CSVLink
                      className="link"
                      data={getCsvData(groupedDob.deliveryOrderBatches)}
                      filename={`delivery-${csvFileTitleDate}.csv`}
                    >
                      Download CSV
                    </CSVLink>
                    <div>
                      <strong>
                        <FormattedMessage id={'delivery.day.users.view.cta.total.count'} />
                      </strong>
                      : {groupedDob.deliveryOrderBatches.length}
                    </div>
                  </td>
                </tr>
                {isHidden
                  ? ''
                  : groupedDob.deliveryOrderBatches.map((dob, index) => {
                      const formattedAddress = dob.address
                        ? `${dob.address.unit ? `Apt ${dob.address.unit}, ` : ''}${dob.address.street} ${
                            dob.address.postCode
                          } ${dob.address.city}`
                        : '';

                      return (
                        <tr key={index}>
                          <th scope="row">
                            <div className="cursor-pointer" onClick={() => setModalAddress(dob.address)}>
                              {dob.packingId ? `#${dob.packingId}` : `D${dob.id}`} - {dob.address.customerName}
                            </div>
                          </th>
                          <td>{formattedAddress}</td>
                          <td>
                            <a href={`tel:${dob.address.phoneNumber}`}>{dob.address.phoneNumber}</a>
                          </td>
                          <td>{dob.address.email}</td>
                        </tr>
                      );
                    })}
                <tr style={{ height: '60px' }}>
                  <td className="admin__table-separator" colSpan="4" />
                </tr>
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

function getCsvData(deliveryOrderBatches) {
  const csvHeaders = [
    'Address/Company name',
    'Address line 1',
    'Address line 2',
    'City',
    'State',
    'Zip',
    'Country',
    'Latitude',
    'Longitude',
    'Earliest time',
    'Latest time',
    'Time at Stop',
    'Notes',
    'Package size',
    'Name',
    'Email address',
    'Phone Number',
    'External ID',
    'Order count',
    'Driver',
  ];

  return [
    csvHeaders,
    ...deliveryOrderBatches.map((dob) => {
      const unit = dob.address.unit ? 'Apt ' + dob.address.unit : '';
      const id = dob && dob.packingId ? `${dob.packingId}` : dob ? `D${dob.id}` : '_';

      return [
        '',
        dob.address.street,
        unit,
        dob.address.city,
        dob.address.state,
        dob.address.postCode,
        dob.address.countryName,
        dob.address.coordinates ? dob.address.coordinates.lat : '',
        dob.address.coordinates ? dob.address.coordinates.lon : '',
        '',
        '',
        '',
        `${id} - ${dob.address.customerName}`,
        '',
        `${id} - ${dob.address.customerName}`,
        '',
        dob.address.phoneNumber,
        `${id}`,
        '',
        '',
      ];
    }),
  ];
}

AdminDeliveryDayViewUsers.propTypes = {
  filteredOrderBatches: PropTypes.array.isRequired,
  deliveryDay: PropTypes.object.isRequired,
  setModalAddress: PropTypes.func.isRequired,
};

AdminDeliveryDayViewUsers.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default injectIntl(connect(mapStateToProps)(AdminDeliveryDayViewUsers));
